import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { SectionFrame } from "../atoms/SectionFrame";
import { AssetPlaceholder } from "../atoms/AssetPlaceholder";
import { GhostButton } from "../atoms/Buttons";
import { ASSETS } from "../../lib/assets";
import { TID } from "../../lib/testIds";

const CARDS = ASSETS.caseStudies;

const EASE = [0.25, 0.1, 0.25, 1];
const EXPANDED_INITIAL = { opacity: 0, height: 0 };
const EXPANDED_ANIMATE = { opacity: 1, height: "auto" };
const EXPANDED_EXIT = { opacity: 0, height: 0 };
const EXPANDED_TRANSITION = { duration: 0.6, ease: EASE };
const BLOCK_INITIAL = { opacity: 0, y: 14 };
const BLOCK_ANIMATE = { opacity: 1, y: 0 };

export function StoriesInMotionSection() {
    const containerRef = useRef(null);
    return (
        <section
            ref={containerRef}
            className="relative -mt-10 sm:-mt-12 z-10"
        >
            <div className="w-full mx-auto px-5 sm:px-8 md:px-10 max-w-[1440px]">
                <div
                    className="relative border bg-[var(--bg)] rounded-t-[40px] sm:rounded-t-[50px] md:rounded-t-[60px] rounded-b-[28px] sm:rounded-b-[36px] overflow-hidden"
                    style={{ borderColor: "var(--line)" }}
                >
                    <div className="px-6 md:px-12 pt-16 md:pt-24 pb-10">
                        <div className="text-base uppercase tracking-[0.3em] mb-4"
                             style={{ color: "var(--accent)" }}>
                            CASE STUDIES
                        </div>
                        <h2
                            data-testid={TID.storiesHeading}
                            className="display-text font-black uppercase"
                            style={{
                                fontSize: "clamp(3rem, 12vw, 150px)",
                                lineHeight: 0.92,
                                letterSpacing: "-0.035em",
                            }}
                        >
                            Stories In<br />Motion
                        </h2>
                    </div>

                    <div className="px-3 sm:px-6 md:px-10 pb-24 md:pb-32">
                        {CARDS.map((c, i) => (
                            <StackingCard
                                key={c.number}
                                data={c}
                                index={i}
                                total={CARDS.length}
                                containerRef={containerRef}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

function StackingCard({ data, index, total, containerRef }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "start start"],
    });
    const targetScale = 1 - (total - 1 - index) * 0.03;
    const scale = useTransform(scrollYProgress, [0, 1], [1, targetScale]);
    const [open, setOpen] = useState(false);

    return (
        <div
            ref={ref}
            className="flex items-start justify-center mb-6 md:mb-0 md:h-[85vh] md:sticky"
            style={{ top: `calc(6rem + ${index * 28}px)` }}
        >
            <motion.div
                data-testid={TID.stackingCard(index)}
                style={{ scale, transformOrigin: "top center" }}
                className="relative w-full border-2 rounded-[40px] sm:rounded-[50px] md:rounded-[60px] p-4 sm:p-6 md:p-8 overflow-visible md:overflow-hidden"
                initial={false}
                animate={{ height: open ? "auto" : undefined }}
            >
                <div className="absolute inset-0 -z-10 rounded-[40px] sm:rounded-[50px] md:rounded-[60px]"
                     style={{
                         background: "#0C0C0C",
                         border: "2px solid var(--line)",
                     }}
                />
                {/* Top row */}
                <div className="flex items-start justify-between gap-4 mb-6 md:mb-8">
                    <div className="flex items-baseline gap-4 md:gap-6">
                        <span
                            className="font-black"
                            style={{
                                color: "var(--muted)",
                                fontSize: "clamp(2rem, 5vw, 5rem)",
                                lineHeight: 1,
                            }}
                        >
                            {data.number}
                        </span>
                        <h4
                            className="display-text font-black uppercase"
                            style={{
                                fontSize: "clamp(1.6rem, 4.5vw, 4rem)",
                                lineHeight: 1,
                                letterSpacing: "-0.02em",
                            }}
                        >
                            {data.client}
                        </h4>
                    </div>
                    <GhostButton
                        label={open ? "Close" : "View Case"}
                        data-testid={TID.stackingViewCase(index)}
                        onClick={() => setOpen((v) => !v)}
                    />
                </div>

                {/* Image grid — adapts to frame count (2 or 3) */}
                {data.frames.length >= 3 ? (
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3 md:gap-4">
                        <div className="md:col-span-2 grid grid-rows-2 gap-3 md:gap-4">
                            <AssetPlaceholder caption={data.frames[0].caption} video={data.frames[0].video} className="w-full h-[180px] md:h-[220px]" rounded="rounded-[28px] md:rounded-[36px]" />
                            <AssetPlaceholder caption={data.frames[1].caption} video={data.frames[1].video} className="w-full h-[180px] md:h-[220px]" rounded="rounded-[28px] md:rounded-[36px]" />
                        </div>
                        <div className="md:col-span-3">
                            <AssetPlaceholder caption={data.frames[2].caption} video={data.frames[2].video} className="w-full h-[372px] md:h-[460px]" rounded="rounded-[28px] md:rounded-[36px]" />
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                        {data.frames.map((fr) => (
                            <AssetPlaceholder
                                key={fr.caption}
                                caption={fr.caption}
                                video={fr.video}
                                className="w-full h-[220px] md:h-[460px]"
                                rounded="rounded-[28px] md:rounded-[36px]"
                            />
                        ))}
                    </div>
                )}

                {/* Expanded blocks */}
                <AnimatePresence initial={false}>
                    {open && (
                        <motion.div
                            key="expanded"
                            initial={EXPANDED_INITIAL}
                            animate={EXPANDED_ANIMATE}
                            exit={EXPANDED_EXIT}
                            transition={EXPANDED_TRANSITION}
                            className="overflow-hidden mt-8 md:mt-10"
                        >
                            <div className="relative grid grid-cols-1 md:grid-cols-5 gap-6 pl-6 md:pl-8">
                                {/* glow line on left */}
                                <div className="absolute left-0 top-2 bottom-2 w-[2px] rounded-full"
                                     style={{
                                         background: "linear-gradient(180deg, transparent, var(--accent), transparent)",
                                         boxShadow: "0 0 12px var(--accent-soft), 0 0 32px var(--accent-soft)",
                                     }} />
                                {[
                                    ["Challenge", data.blocks.challenge],
                                    ["Strategy", data.blocks.strategy],
                                    ["Storyboard", data.blocks.storyboard],
                                    ["Motion", data.blocks.motion],
                                    ["Outcome", data.blocks.outcome],
                                ].map(([label, line], i) => (
                                    <motion.div
                                        key={label}
                                        initial={BLOCK_INITIAL}
                                        animate={BLOCK_ANIMATE}
                                        transition={{ delay: 0.08 * i, duration: 0.5, ease: EASE }}
                                    >
                                        <div className="text-sm uppercase tracking-[0.25em] mb-2"
                                             style={{ color: "var(--accent)" }}>
                                            {label}
                                        </div>
                                        <p className="text-base md:text-base" style={{ color: "var(--text)" }}>
                                            {line}
                                        </p>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
    );
}
